import type { StockDetail, StockIndicator } from '../../types'

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtBig(v: number | null | undefined, cur: string) {
    if (v == null) return '—'
    const sym = cur === 'HKD' ? 'HK$' : cur === 'USD' ? '$' : '¥'
    if (Math.abs(v) >= 1e12) return sym + (v / 1e12).toFixed(2) + '万亿'
    if (Math.abs(v) >= 1e8)  return sym + (v / 1e8).toFixed(2)  + '亿'
    if (Math.abs(v) >= 1e4)  return sym + (v / 1e4).toFixed(2)  + '万'
    return sym + v.toFixed(2)
}

function fmtNum(v: number | null | undefined, dp = 2, suffix = '') {
    if (v == null) return '—'
    return v.toFixed(dp) + suffix
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FinCard({ label, val, sub, pos }: {
    label: string; val: string; sub?: string; pos?: boolean
}) {
    const isDash = val === '—'
    return (
        <div className="skd__fin-card">
            <div className="skd__fin-lbl">{label}</div>
            <div className="skd__fin-val" style={isDash ? { color: 'var(--text-3)' } : undefined}>
                {val}
            </div>
            {sub && (
                <div className={`skd__fin-sub${pos === undefined ? '' : pos ? ' pos' : ' neg'}`}>
                    {sub}
                </div>
            )}
        </div>
    )
}

function KpiBlock({ data }: { data: Record<string, string> | null | undefined }) {
    if (!data) return null
    const entries = Object.entries(data)
    if (!entries.length) return null
    return (
        <div className="skd__kpi-grid">
            {entries.map(([k, v]) => (
                <div key={k} className="skd__kpi-item">
                    <div className="skd__kpi-item-lbl">{k}</div>
                    <div className="skd__kpi-item-val">{v}</div>
                </div>
            ))}
        </div>
    )
}

function StepBlock({ title, content }: { title: string; content: string }) {
    let display = content.trim()
    if (display.startsWith('```')) {
        display = display.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '')
        try { display = JSON.stringify(JSON.parse(display), null, 2) } catch { /* keep as-is */ }
    }
    return (
        <div className="skd__block">
            <div className="skd__block-title">{title}</div>
            <pre className="skd__raw-block">{display}</pre>
        </div>
    )
}

// ── Constants ─────────────────────────────────────────────────────────────────

const RT_LABEL: Record<string, string> = {
    annual:       '年报',
    interim_h1:   '中报(H1)',
    interim_h2:   '中报(H2)',
    quarterly_q1: 'Q1季报',
    quarterly_q2: 'Q2季报',
    quarterly_q3: 'Q3季报',
    quarterly_q4: 'Q4季报',
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function Overview({ stock }: { stock: StockDetail }) {
    if (!stock.indicators.length) return null
    const ind: StockIndicator = stock.indicators[0]
    const cur = ind.currency ?? 'HKD'

    const rtLabel = RT_LABEL[ind.report_type ?? ''] ?? ind.report_type ?? '—'

    const revYoy = ind.revenue_yoy_pct
    const cfo    = ind.operating_cash_flow_cfo

    return (
        <div className="skd__overview">

            {/* ── 1. 财务快照 ───────────────────────────────────────────── */}
            <div className="skd__block">
                <div className="skd__block-title">
                    财务快照
                    <span className="skd__block-sub">
                        {ind.fiscal_year ?? '—'} · {rtLabel}
                        {ind.report_date ? ` · ${ind.report_date}` : ''}
                    </span>
                </div>
                <div className="skd__val-row">
                    <FinCard
                        label="营业收入"
                        val={fmtBig(ind.revenue, cur)}
                        sub={revYoy != null ? `YoY ${revYoy >= 0 ? '+' : ''}${revYoy.toFixed(1)}%` : undefined}
                        pos={revYoy != null ? revYoy >= 0 : undefined}
                    />
                    <FinCard
                        label="毛利率"
                        val={fmtNum(ind.gross_margin_pct, 1, '%')}
                        pos={true}
                    />
                    <FinCard
                        label="归母净利润"
                        val={fmtBig(ind.net_profit_parent, cur)}
                        sub={ind.net_margin_pct != null ? `净利率 ${ind.net_margin_pct.toFixed(1)}%` : undefined}
                        pos={ind.net_profit_parent != null ? ind.net_profit_parent >= 0 : undefined}
                    />
                    <FinCard
                        label="经营现金流"
                        val={fmtBig(cfo, cur)}
                        pos={cfo != null ? cfo >= 0 : undefined}
                    />
                </div>
            </div>

            {/* ── 2. 收入来源 ────────────────────────────────────────────── */}
            {ind.revenue_sources && (
                <div className="skd__block">
                    <div className="skd__block-title">收入来源</div>
                    <p className="skd__events-text">{ind.revenue_sources}</p>
                </div>
            )}

            {/* ── 3. 增长驱动 ────────────────────────────────────────────── */}
            {ind.revenue_growth_drivers && (
                <div className="skd__block">
                    <div className="skd__block-title">增长驱动</div>
                    <p className="skd__events-text">{ind.revenue_growth_drivers}</p>
                </div>
            )}

            {/* ── 4. 业务运营指标 ─────────────────────────────────────────── */}
            {ind.kpi_metrics && Object.keys(ind.kpi_metrics).length > 0 && (
                <div className="skd__block">
                    <div className="skd__block-title">业务运营指标</div>
                    <KpiBlock data={ind.kpi_metrics} />
                </div>
            )}

            {/* ── 5. 非GAAP指标 ───────────────────────────────────────────── */}
            {ind.kpi_non_gaap_metrics && Object.keys(ind.kpi_non_gaap_metrics).length > 0 && (
                <div className="skd__block">
                    <div className="skd__block-title">非GAAP指标</div>
                    <KpiBlock data={ind.kpi_non_gaap_metrics} />
                </div>
            )}

            {/* ── 6. 管理层前瞻指引 ───────────────────────────────────────── */}
            {ind.kpi_guidance && Object.keys(ind.kpi_guidance).length > 0 && (
                <div className="skd__block">
                    <div className="skd__block-title">管理层前瞻指引</div>
                    <KpiBlock data={ind.kpi_guidance} />
                </div>
            )}

            {/* ── 7. 重大事项 ────────────────────────────────────────────── */}
            {ind.annual_report_major_events && (
                <div className="skd__block">
                    <div className="skd__block-title">重大事项</div>
                    <p className="skd__events-text">{ind.annual_report_major_events}</p>
                </div>
            )}

            {/* ── 8. AI 提取数据 Step 1 ──────────────────────────────────── */}
            {ind.indicators_step_1_md && (
                <StepBlock title="AI 提取数据（Step 1）" content={ind.indicators_step_1_md} />
            )}

            {/* ── 9. AI 提取数据 Step 2 ──────────────────────────────────── */}
            {ind.indicators_step_2_md && (
                <StepBlock title="AI 提取数据（Step 2）" content={ind.indicators_step_2_md} />
            )}

        </div>
    )
}
